import * as React from "react";
import { createPortal } from "react-dom";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";

import { cn } from "@/shared/lib/utils";

const ToastProvider = ToastPrimitives.Provider;

/**
 * toast 容器 —— **必须挂在 `document.body` 上**(2026-07-30,7.29 反馈任务 4b)。
 *
 * 默认它渲染在 React 树原地,也就是 `#root` 里面。只要有人给 `#root` 加一个 z-index
 * (三套皮肤都曾各加过一份 `z-index: 2` 去压 fixed 装饰),`#root` 就自成层叠上下文、
 * 对外只有那一个层级 —— 下面这个 `z-[100]` 就**只在 #root 内部有效**了。而 Radix Dialog
 * 是走 Portal 挂到 body 的、`z-50`,在根层叠上下文里 50 > 2 直接赢:弹窗里冒出来的 toast
 * 被遮罩压暗、被 backdrop-blur 糊住,连关闭按钮都点不到(租户侧每个弹窗都中)。
 *
 * 皮肤那三条 z-index 已经删了,但**根因是「toast 与 Dialog 不在同一个层叠上下文」**。
 * 挂到 body 让两者同处根上下文,z-index 数值才真的可比 —— 将来谁再给 #root 加层级也不会
 * 退回去。★判断这条有没有生效**不要比 z-index 数值**(100 > 50 但结果是反的,已被骗过一次),
 * 用像素:弹窗打开时 toast 区域的边缘能量。
 */
const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => {
  const viewport = (
    <ToastPrimitives.Viewport
      ref={ref}
      className={cn(
        "fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]",
        className
      )}
      {...props}
    />
  );
  // SSR / 测试环境无 document 时退回原地渲染(不为了加固把渲染搞崩)。
  return typeof document === "undefined" ? viewport : createPortal(viewport, document.body);
});
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

// 配色走皮肤 token(bg-background/text-foreground/border/destructive),3 套皮肤都可读。
// 此前硬编码 cyber 深色渐变 + 霓虹光 + text-foreground → standard/comic(浅色皮肤)下
// 深底配深字看不清。cyber 的霓虹外观改由 skin-cyber.css `[data-skin="cyber"] .cyber-toast`
// 覆写(保留 cyber-toast 类作钩子),只在 cyber 皮肤生效。
const toastVariants = cva(
  [
    "cyber-toast group pointer-events-auto relative flex w-full items-center justify-between space-x-4",
    "overflow-hidden rounded-lg border p-5 pr-10 shadow-lg transition-all",
    "data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)]",
    "data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none",
    "data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out",
    "data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full",
    "data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full",
    "data-[state=open]:duration-500",
  ].join(" "),
  {
    variants: {
      variant: {
        default: "border-border bg-background text-foreground",
        destructive:
          "destructive group border-destructive bg-destructive text-destructive-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    />
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors group-[.destructive]:border-muted/40 hover:bg-secondary group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 group-[.destructive]:focus:ring-destructive disabled:pointer-events-none disabled:opacity-50",
      className
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      "absolute right-2 top-2 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity group-hover:opacity-100 group-[.destructive]:text-red-300 hover:text-foreground group-[.destructive]:hover:text-red-50 focus:opacity-100 focus:outline-none focus:ring-2 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600",
      className
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title ref={ref} className={cn("text-sm font-semibold", className)} {...props} />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn("text-sm opacity-90", className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
};
